/**
 * Gráfica de barras simple (presentacional, Tailwind).
 *
 * El SSR (`advanced_stats_with_charts.html`) dibujaba estas series con Chart.js
 * sobre `<canvas>`. La SPA replica el **contenido** (las mismas series y valores de
 * la API), no la librería: se renderiza una barra horizontal por punto, con su
 * etiqueta y su valor, escalando el ancho al máximo de la serie. Es funcional,
 * accesible (lista con roles nativos) y testeable sin añadir una dependencia de
 * charts al stack (mismo criterio que la #61, que presentó los desgloses con
 * chips/listas Tailwind).
 *
 * Sin lógica de negocio ni llamadas a la API (`docs/frontend_architecture.md`,
 * Pr. 6): recibe las barras ya derivadas y solo presenta.
 */
import type { BarDatum } from "../format";

interface BarChartProps {
  /** Barras a pintar (etiqueta + valor + texto opcional ya formateado). */
  data: BarDatum[];
  /** Color Tailwind del relleno de la barra (por defecto, azul de marca). */
  barClassName?: string;
  /** Texto cuando la serie viene vacía (no pinta barras). */
  emptyMessage?: string;
}

/** Color de relleno por defecto de las barras (azul de marca del proyecto). */
const DEFAULT_BAR = "bg-brand";

export function BarChart({
  data,
  barClassName = DEFAULT_BAR,
  emptyMessage = "Sin datos en el período.",
}: BarChartProps) {
  if (data.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-slate-500">{emptyMessage}</p>
    );
  }

  // Escala: el máximo de la serie ocupa el 100% del ancho. Evita dividir por cero.
  const max = Math.max(...data.map((datum) => datum.value), 0);

  return (
    <ul className="flex flex-col gap-2">
      {data.map((datum, index) => {
        const ratio = max > 0 ? (datum.value / max) * 100 : 0;
        const display = datum.display ?? String(datum.value);
        return (
          <li key={`${datum.label}-${index}`} className="flex items-center gap-3">
            <span className="w-20 shrink-0 truncate text-xs font-medium text-slate-500">
              {datum.label}
            </span>
            <div className="h-4 flex-1 overflow-hidden rounded bg-slate-100">
              <div
                className={`h-full rounded ${barClassName}`}
                style={{ width: `${ratio}%` }}
              />
            </div>
            <span className="w-16 shrink-0 text-right text-xs font-semibold text-slate-700">
              {display}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
